/**
 * Shell — the DEFAULT connect->play shell (cutover 2026-07-09).
 *
 * The shipped four-screen pre-game experience realized from the storyboard at
 * frontend/web/preview/login-sequence/. Lobby, World-Create, Character-Choice,
 * and the read-box+sigil warming surface, rendered to the ASCII x square visual
 * canon (docs/Tome/_meta/ui-visual-canon.md).
 *
 * CUTOVER (was ?shell=1 flag-gated; see .claude_specs/engine/frontend-slice1-shell/build-note.md):
 *   - The shell is now the DEFAULT post-login experience. `init()` activates on
 *     every normal login with NO query param and NO fallback. The old terminal-
 *     driven world/character menu is no longer reachable by any user path (it is
 *     STRIPPED in an immediate follow-up PR; rollback is by image-digest /
 *     hosting-release, not by a dead runtime path). There is NO `?shell=0`
 *     kill-switch and NO runtime fallback by design.
 *   - The SOLE suppressor is the VP fast-path (`?fastpath=1`, or a committed
 *     dev-allowlist resume): fastpath auto-resumes straight to GAMEPLAY and must
 *     bypass the pre-game screens entirely, so `isActive()` yields to it.
 *   - A full-screen `#shell-root` overlay; the live terminal (`.main`) is hidden
 *     while a pre-game screen is up and revealed on GAMEPLAY. The existing
 *     arrival/ch0 path renders untouched underneath -> NO collision with the
 *     in-flight arrival-server-authoritative rework (ADR 2026-07-06-001 / PR#317).
 *   - `gameserver-connection.js` is NOT edited. The shell reads the connect/queue/
 *     roster seams via FSMEvents subscription + three call-through monkey-patch
 *     wrappers installed only when active.
 *
 * FSM mapping (reuses existing states, invents none):
 *   Lobby         <- DISCONNECTED (authenticated, pre-connect world browsing)
 *   World Create  <- DISCONNECTED (drives the HTTP create flow)
 *   Read-box      <- CONNECTING / QUEUED / WELCOME_NARRATION / ROSTER_DISPLAY / SPAWNING
 *   Char Choice   <- CHARACTER_CHOICE
 *   Terminal      <- GAMEPLAY (handoff to the existing terminal)
 */

import WorldCommands from '../world-commands.js';
import WorldList from '../world-list.js';
import UnifiedAuthService from '../unified-auth.js';
import { DecoderSigil } from './sigil.js';
import { ReadBox } from './lore.js';
import { launchContext } from '../platform/platform.js';

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
}

// ---- PAX build ceiling ----------------------------------------------------
// A world seats EIGHT for the PAX build, and the password option is withdrawn.
//
// Both are rendered GREYED AND PRESENT rather than removed: the shape of the
// choice stays legible, so a player reads "not this build" instead of "this
// product has no such setting". That is the same call game-hud.css records for
// the attribute stepper and the world-map buttons -- "a DISABLED control stays
// visible and goes quiet".
//
// ⚠️ THE GREY IS ENFORCED IN BEHAVIOUR, NOT IN CSS. Every listener below tests
// the `disabled` class before it does anything, because a control that is only
// grey-LOOKING still fires its handler, and the whole point of the ceiling is
// that `state.maxPlayers` cannot leave this screen as anything but 8. A CSS
// regression (or a stylesheet that fails to load -- see the guarded
// `import('../../styles/shell.css')` in `init()`, which is allowed to fail and
// continue) must not be able to re-open the option.
const PAX_MAX_PLAYERS = 8;
const MAX_PLAYER_CHOICES = [4, 8, 16, 32];
// shell.css already spells "inert" as a `disabled` CLASS (`.readbox-foot
// .pager-btn.disabled`), so that is the class used here and the one a future
// `.chip.disabled` / `.input.disabled` rule should hang off. There is no such
// rule today and this change may not edit shell.css, so the greying itself
// rides inline -- the same idiom as the `style="font-size:1.3rem"` brand line
// in this screen's own template. Move it to shell.css when that file is free.
const INERT_STYLE = 'opacity:0.35;cursor:default;pointer-events:none;';

const Shell = {
    _active: false,
    _root: null,
    _screenEl: null,
    _stateEl: null,
    _sigil: null,
    _readbox: null,
    _roster: null,            // captured roster payload (from the displayCharacterChoices patch)
    _screen: null,            // current screen name
    _patched: false,

    // ---- login queue (QUEUE BEFORE FAILURE) --------------------------------
    // The cutover's whole point: on a COLD backend the player is QUEUED, not
    // failed. Before this, every cold-path outcome on login was a dead end:
    //
    //   * `connectToGameServer` is FIRE-AND-FORGET — it creates the WebSocket
    //     and returns. `await` resolves immediately, so the shell never learned
    //     whether the connect succeeded.
    //   * A socket that never opens fires `onclose` (-> internal reconnect) and
    //     `onerror` (-> console.error only). There is NO FSM transition on a
    //     failed connect, so the shell sat on the CONNECTING read-box —
    //     "Reaching the world…" — forever.
    //   * The QUEUE surface (`terminal:queued`) can only fire from a LIVE socket
    //     (the GameServer's admission controller sends `queue_position`). A cold
    //     GameServer/GANDALF never gets that far, so the queue screen was
    //     unreachable exactly when it was needed.
    //   * The API legs (`_join` / `_continue` / world-list) retried 3x over ~7s
    //     and then printed "Join failed." A cold start takes far longer than 7s.
    //
    // So: hold the player on the WARMING surface and KEEP TRYING. Only a
    // genuinely unrecoverable error (auth) fails fast; everything else queues.
    _progressed: false,       // set by any post-connect FSM state
    _queueAbort: false,
    _queueActive: false,      // a login queue is holding the screen (suppresses the disconnected->Lobby bounce)
    _lastAttempt: null,       // the in-flight attempt fn, so KEEP WAITING can re-run it

    // If the connect makes no FSM progress within this, treat it as COLD and
    // queue (do not fail). A warm connect reaches CONNECTING->... in well under
    // a second; 12s is a cold-start signal, not a hang.
    _CONNECT_PROGRESS_MS: 12000,
    // How long we hold a player on the warming screen before admitting defeat.
    //
    // 20 MINUTES, deliberately. Sized to the WORST case we actually ship:
    // warming GANDALF from a COLD START (scaled to zero). That is not the normal
    // experience — a warm stack connects in under a second — and it only happens
    // in two situations, both of which we would rather QUEUE through than fail:
    //
    //   1. F&F, where the pods have scaled to zero between sessions.
    //   2. A PAX spike, where we are actively requisitioning more server
    //      reservations and capacity is arriving behind the player.
    //
    // In both, the player's world IS coming — it is just not there yet. Failing
    // them at 4 minutes would drop them seconds before the thing they waited for.
    // A player who is TOLD they are queued will wait; a player dumped back to a
    // world list with no explanation is gone.
    //
    // ⚠️ Do not shrink this to make a test faster or a cold start "feel"
    // shorter — the tests override it explicitly, and shortening it here only
    // converts a long wait into a lost session.
    _WARM_CEILING_MS: 1200000,
    // Retry backoff while warming. Tunable (and shrinkable in tests) rather
    // than buried as a literal in the loop.
    _QUEUE_BASE_DELAY_MS: 2000,
    _QUEUE_MAX_DELAY_MS: 15000,

    // ---- activation -------------------------------------------------------

    isActive() {
        // Cutover: the shell is the DEFAULT connect->play experience. It is active
        // on every normal login with no query param. The ONLY thing that suppresses
        // it is the VP fast-path, which auto-resumes straight to GAMEPLAY and must
        // bypass the pre-game screens entirely -- fastpath wins.
        try {
            if (new URLSearchParams(window.location.search).get('fastpath') === '1') {
                return false; // deterministic fast-path trigger: shell stays inert
            }
        } catch (_e) {
            // fall through -> shell active
        }
        // Dev-allowlist fast-path resume already committed (set synchronously by
        // Fastpath.maybeRun when it takes over a restored/allowlisted session).
        if (typeof window !== 'undefined' && window.__REAVERS_FASTPATH_ACTIVE__) {
            return false;
        }
        return true;
    },

    async init() {
        if (!this.isActive()) {
            return; // fast-path owns this load: shell stays inert, terminal resumes
        }
        this._active = true;
        window.__REAVERS_SHELL_ACTIVE__ = true;
        console.log('[shell] active (default connect->play shell) -- overlaying pre-game screens');

        // Load the shell stylesheet as a separate on-demand chunk (default users
        // never fetch it). All rules are scoped under #shell-root.
        try {
            await import('../../styles/shell.css');
        } catch (err) {
            console.warn('[shell] shell.css import failed (continuing):', err);
        }

        this._buildOverlay();
        this._installSeamPatches();
        this._subscribeFsm();

        // Boot: Lobby once authenticated. If auth is not yet ready, show a status
        // line and paint the Lobby when the auth state arrives. (An unauthenticated
        // user is redirected to login by unified-auth as usual -- not suppressed.)
        if (UnifiedAuthService && UnifiedAuthService.isAuthenticated && UnifiedAuthService.isAuthenticated()) {
            this._boot();
        } else {
            this._renderStatus('Authenticating…');
            if (UnifiedAuthService && UnifiedAuthService.onAuthStateChanged) {
                UnifiedAuthService.onAuthStateChanged((user) => {
                    if (!user) return;
                    // A dev-allowlist fast-path may commit as auth arrives (its
                    // listener is registered before the shell's, so it wins the
                    // race and sets the flag first). If so, yield the overlay.
                    if (window.__REAVERS_FASTPATH_ACTIVE__) {
                        this._yieldToFastpath();
                        return;
                    }
                    if (this._screen == null) this._boot();
                });
            }
        }
    },

    /**
     * The first screen: a platform launch lands where the server says (the
     * host's world, or a shared link's), otherwise the Lobby. Runs once.
     */
    async _boot() {
        if (this._booted) return;
        this._booted = true;
        if (await this._landLaunch()) return;
        this.showLobby();
    },

    /** True when a platform launch owns the screen (landed, or refused loudly). */
    async _landLaunch() {
        const ctx = await launchContext();
        if (!ctx) return false;
        this._renderStatus('Finding your world...');
        let resp;
        try {
            resp = await WorldCommands.callWorldAPI('/api/worlds/launch', 'POST', ctx,
                { maxRetries: 0, showRetryMessages: false });
        } catch (err) {
            console.error('[shell] launch refused:', err);
            this._lobbyError(`This launch could not be landed: ${err.message || err}`);
            return true;
        }
        const launch = resp && resp.launch;
        console.log(`[shell] launch.land path=${launch && launch.path} world=${launch && launch.world_id}`);
        if (!launch || !launch.world_id) return false;
        await this._join(launch.world_id, launch.join_token);
        return true;
    },

    /** Tear down the overlay and reveal the terminal when the fast-path takes over. */
    _yieldToFastpath() {
        console.log('[shell] fast-path committed -- yielding overlay to the resuming terminal');
        this._active = false;
        window.__REAVERS_SHELL_ACTIVE__ = false;
        this._clearScreen();
        this._showOverlay(false);
        if (this._root && this._root.parentNode) {
            this._root.parentNode.removeChild(this._root);
            this._root = null;
        }
    },

    _buildOverlay() {
        const root = document.createElement('div');
        root.id = 'shell-root';
        root.innerHTML = `
            <div class="shell-bar">
                <span class="shell-logo">&lt;|REAVERS|&gt;</span>
                <span class="shell-state" id="shell-state"></span>
            </div>
            <div class="shell-screen" id="shell-screen"></div>`;
        document.body.appendChild(root);
        this._root = root;
        this._screenEl = root.querySelector('#shell-screen');
        this._stateEl = root.querySelector('#shell-state');
        this._showOverlay(true);
    },

    _showOverlay(show) {
        if (this._root) this._root.classList.toggle('hidden', !show);
        const main = document.querySelector('.main');
        if (main) main.style.display = show ? 'none' : '';
    },

    _setState(label) {
        if (this._stateEl) this._stateEl.textContent = label || '';
    },

    _clearScreen() {
        if (this._sigil) { this._sigil.destroy(); this._sigil = null; }
        this._readbox = null;
        if (this._screenEl) this._screenEl.innerHTML = '';
    },

    _renderStatus(msg) {
        this._clearScreen();
        const el = document.createElement('div');
        el.className = 'lobby-status';
        el.textContent = msg;
        this._screenEl.appendChild(el);
    },

    // ---- FSM wiring -------------------------------------------------------

    _subscribeFsm() {
        const on = (evt, fn) => window.FSMEvents && window.FSMEvents.on(evt, fn);

        // Any state PAST the connect means the socket is live and the FSM owns
        // the screen from here — the login queue must stand down. This is the
        // ONLY honest "the connect worked" signal available, because
        // connectToGameServer is fire-and-forget and cannot tell us itself.
        const progress = () => { this._progressed = true; };

        on('terminal:connecting', () => this.showReadBox('CONNECTING', 'Reaching the world…'));
        on('terminal:queued', () => {
            progress();
            this.showReadBox('WARMING', 'Servers warming — read on while the world spins up.');
        });
        on('terminal:welcome_narration', () => {
            progress();
            this.showReadBox('ENTERING', 'A voice stirs in the dark…');
        });
        on('terminal:roster_display', () => {
            progress();
            this.showReadBox('ENTERING', 'Preparing your choices…');
        });
        // [2026-07-29-012-QASTI] `prev` distinguishes a FIRST JOIN
        // (ROSTER_DISPLAY -> CHARACTER_CHOICE) from a RECLAIM after death
        // (GAMEPLAY -> CHARACTER_CHOICE). They are not the same screen: one is a
        // beginning, the other follows "You stagger, then fall. Defeated." and
        // was byte-identical to it, down to "Choose, and step into the world."
        on('terminal:character_choice', (d) => {
            progress();
            // The void asks one of two ways: the Episode board (and the hand
            // dealt for one of its roads), or the ordinary hand.
            if (this._boardSelection) {
                this.showBoard();
                return;
            }
            this.showCharacterChoice({ reclaim: !!(d && d.prev === 'GAMEPLAY') });
        });
        on('terminal:spawning', () => {
            progress();
            this.showReadBox('READY', 'Stepping into the world…', true);
        });
        on('terminal:gameplay', () => { progress(); this._boardSelection = null; this._handoffToTerminal(); });
        on('terminal:disconnected', () => {
            // Returned to the front door (leave / failed connect). Re-show the Lobby
            // only if the overlay is up (i.e. we're not mid-teardown of the page).
            // ⚠️ If a login queue is in flight, do NOT bounce to the Lobby — the
            // queue owns the screen and is still retrying. Bouncing here is what
            // turned a cold start into a dead end.
            if (this._active && !this._queueActive) this.showLobby();
        });
    },

    // ---- the login queue --------------------------------------------------

    /** Sleep that a queue abort can cut short. */
    _sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    },

    /** m:ss elapsed. `(1140s)` is not a number a waiting human can read. */
    _elapsed(ms) {
        const total = Math.max(0, Math.round(ms / 1000));
        const m = Math.floor(total / 60);
        const s = total % 60;
        return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
    },

    /**
     * What we tell a queued player, and it changes as the wait grows.
     *
     * A silent spinner is what makes people leave. Say what is happening, say it
     * is expected, and keep the clock visible — a player who KNOWS they are
     * queued will wait; a player left guessing will not.
     */
    _warmHint(waitedMs) {
        const t = this._elapsed(waitedMs);
        if (waitedMs < 30000) {
            return `Waking the world. This usually takes a few seconds. (${t})`;
        }
        if (waitedMs < 150000) {
            return `The servers were asleep — a cold start takes a minute or two. ` +
                   `You will go in automatically. (${t})`;
        }
        return `Still warming. Nothing is wrong — capacity is coming up behind you, ` +
               `and you keep your place in the queue. (${t})`;
    },

    /**
     * Make the login queue the SINGLE retry authority.
     *
     * GameServerConnection has its OWN exponential-backoff reconnect loop. It is
     * the right thing for a mid-game connection LOSS, but during login it (a)
     * races our retry and can stack sockets, and (b) when it gives up it prints
     * "Use /world reconnect to try connecting again" — to the TERMINAL, which is
     * hidden BEHIND this overlay, so the player never sees it.
     *
     * So before each queued re-attempt: cancel its timer, reset its counters, and
     * close any half-open socket. One loop, one socket, one surface.
     */
    _resetConnection() {
        const GSC = window.GameServerConnection;
        if (!GSC || !GSC.state) return;
        try {
            if (GSC.state.reconnectTimer) {
                clearTimeout(GSC.state.reconnectTimer);
                GSC.state.reconnectTimer = null;
            }
            GSC.state.reconnectAttempts = 0;
            GSC.state.isReconnecting = false;
            const ws = GSC.state.websocket;
            if (ws && ws.readyState !== 3 /* CLOSED */) {
                ws.onclose = null;   // don't let the teardown re-enter handleConnectionLoss
                ws.onerror = null;
                try { ws.close(); } catch (_e) { /* already closing */ }
            }
            GSC.state.websocket = null;
        } catch (err) {
            console.warn('[shell] _resetConnection:', err);
        }
    },

    /**
     * Is this error a COLD-BACKEND signal (queue + retry), or a real refusal?
     *
     * Bias is deliberate: anything that is not an explicit auth refusal is
     * treated as WARMING and retried. On a launch-day cold start, a false
     * "queue" costs a few seconds of a warming screen; a false "fail" costs the
     * player the session. Queue before failure.
     */
    _isHardFailure(err) {
        // [2026-08-08-005-QASTI] A server REFUSAL is terminal, not cold.
        // The join door now refuses a world that cannot seat the player
        // (roster exhausted/unprovisioned); retrying it in the warm queue
        // is the exact futile-retry loop the refusal exists to end.
        if (err && err.refused === true) return true;
        const msg = String((err && err.message) || err || '');
        return /Authentication failed|Access forbidden|401|403/i.test(msg);
    },

    /**
     * Wait for the FSM to show ANY sign of life past the connect.
     * Resolves true on progress, false on timeout.
     */
    async _awaitConnectProgress(ms) {
        const deadline = Date.now() + ms;
        while (Date.now() < deadline) {
            if (this._progressed) return true;
            if (this._queueAbort) return false;
            await this._sleep(150);
        }
        return this._progressed;
    },

    /**
     * QUEUE BEFORE FAILURE. Run `attempt` (a join/continue/create-connect) and,
     * if the backend is cold, hold the player on the WARMING surface and keep
     * retrying — instead of printing "Join failed." or stranding them on a
     * CONNECTING box that never resolves.
     *
     * `attempt` must perform the API call AND kick the connect. It returns
     * falsy to mean "the API refused" (we then decide queue-vs-fail); a throw is
     * classified the same way.
     */
    async _queueUntilConnected(label, attempt) {
        this._queueActive = true;
        this._queueAbort = false;
        const deadline = Date.now() + this._WARM_CEILING_MS;
        let delay = this._QUEUE_BASE_DELAY_MS;
        let everWarmed = false;

        try {
            while (!this._queueAbort) {
                this._progressed = false;
                let refused = null;
                try {
                    // One retry loop, one socket. Kill any half-open socket and any
                    // reconnect GSC scheduled for itself before we try again.
                    this._resetConnection();
                    const ok = await attempt();
                    if (ok !== false) {
                        // connectToGameServer is fire-and-forget: the ONLY proof
                        // it worked is the FSM moving. Wait for it.
                        if (await this._awaitConnectProgress(this._CONNECT_PROGRESS_MS)) {
                            return true;  // live socket — the FSM owns the screen now
                        }
                    }
                } catch (err) {
                    if (this._isHardFailure(err)) {
                        this._lobbyError(err.message || `${label} failed.`);
                        return false;
                    }
                    refused = err;
                }

                if (Date.now() >= deadline) {
                    this._queueExhausted(label, refused);
                    return false;
                }

                everWarmed = true;
                const waitedMs = this._WARM_CEILING_MS - (deadline - Date.now());
                this.showReadBox(
                    'WARMING',
                    `Servers are warming up — you're in the queue, holding your place.` +
                    `<br><span class="queue-hint">${this._warmHint(waitedMs)}</span>`,
                );
                await this._sleep(delay);
                delay = Math.min(Math.round(delay * 1.6), this._QUEUE_MAX_DELAY_MS);
            }
            return false;
        } finally {
            this._queueActive = false;
            if (everWarmed) console.log(`[shell] login queue for ${label} ended`);
        }
    },

    /** The warming ceiling was reached — now, and only now, do we fail. */
    _queueExhausted(label, lastErr) {
        this._clearScreen();
        this._screen = 'lobby';
        const box = document.createElement('div');
        box.className = 'shell-lobby';
        box.innerHTML = `
            <div class="lobby-title">STILL WARMING</div>
            <div class="form-error">
                The world is taking longer than expected to come up.
                ${lastErr ? `<br><span class="queue-hint">${escapeHtml(String(lastErr.message || lastErr))}</span>` : ''}
            </div>
            <div class="lobby-actions">
                <button class="btn-ghost" id="shell-requeue">KEEP WAITING</button>
                <button class="btn-ghost" id="shell-back">BACK TO WORLDS</button>
            </div>`;
        this._screenEl.appendChild(box);
        const again = box.querySelector('#shell-requeue');
        const back = box.querySelector('#shell-back');
        if (again) again.addEventListener('click', () => {
            if (this._lastAttempt) this._queueUntilConnected(label, this._lastAttempt);
        });
        if (back) back.addEventListener('click', () => this.showLobby());
    },

    /** Cancel any in-flight login queue (e.g. the player navigates away). */
    _abortQueue() {
        this._queueAbort = true;
        this._queueActive = false;
    },

    /** GAMEPLAY reached: hide the shell, reveal the live terminal untouched. */
    _handoffToTerminal() {
        console.log('[shell] GAMEPLAY -- handing off to the live terminal');
        this._clearScreen();
        this._showOverlay(false);
    },

    // ---- seam patches (install once; all call through) --------------------

    _installSeamPatches() {
        if (this._patched) return;
        const GSC = window.GameServerConnection;
        if (!GSC) {
            console.warn('[shell] GameServerConnection not present -- roster/queue seams unpatched');
            return;
        }
        this._patched = true;

        // (1) Roster capture. displayCharacterChoices renders the roster into the
        // (hidden) terminal #history; when the shell is active we capture the
        // payload and SKIP the terminal render (the FSM transition happens in the
        // caller, handleCreationFlow, so nothing downstream is affected).
        const origDisplay = GSC.displayCharacterChoices.bind(GSC);
        GSC.displayCharacterChoices = (message) => {
            if (this._active) {
                this._roster = message;
                // A hand dealt for a road is the board's last step; any other
                // hand ends the board.
                this._boardSelection = message.cast ? { hand: message } : null;
                return; // shell renders on terminal:character_choice
            }
            return origDisplay(message);
        };

        // (1b) Board capture, the same seam: the board renders here, never in
        // the hidden terminal (episodes.md 7.2).
        const origBoard = GSC.displayEpisodeBoard.bind(GSC);
        GSC.displayEpisodeBoard = (message) => {
            if (this._active) {
                this._boardSelection = { board: message.payload };
                return; // shell renders on terminal:character_choice
            }
            return origBoard(message);
        };

        // (2) Queue-frame mirror. _renderQueueSurface paints the (hidden) terminal
        // queue block; mirror the position/eta/message into the shell read-box.
        const origQueue = GSC._renderQueueSurface.bind(GSC);
        GSC._renderQueueSurface = (message) => {
            const r = origQueue(message);
            if (this._active) this._updateQueue(message);
            return r;
        };
    },

    // ---- screen: Lobby ----------------------------------------------------

    async showLobby() {
        this._screen = 'lobby';
        this._setState('lobby');
        this._showOverlay(true);
        this._renderStatus('Loading worlds…');

        // Browse EVERY open world via the shared accessor (paginates to
        // exhaustion). A freshly created world sits on page 1+ of the unsorted
        // list; reading only page 0 (the old behaviour) silently hid it from a
        // second player -- 2026-07-12-004-ENGINE. fetchAllOpenWorlds shows them all.
        let openWorlds = [];
        let worldsTruncated = false;
        try {
            const { worlds, truncated } = await WorldList.fetchAllOpenWorlds({
                database: WorldCommands.state.selectedDatabase,
            });
            openWorlds = worlds;
            worldsTruncated = truncated;
        } catch (err) {
            console.error('[shell] world list failed:', err);
        }

        // "Your worlds" needs GET /api/characters/mine (NET-NEW, out of scope).
        // Fetch defensively; on any failure hide the section (graceful degrade).
        let myWorlds = [];
        try {
            const mine = await WorldCommands.callWorldAPI('/api/worlds/characters/mine', 'GET', {});
            if (mine && mine.success && Array.isArray(mine.characters)) myWorlds = mine.characters;
        } catch (_e) {
            myWorlds = []; // endpoint absent today -> section hidden
        }

        // DEDUPE the two lists (2026-08-06-013-XSTITCH). A world you already
        // hold a character in satisfied BOTH queries and rendered twice — once
        // as CONTINUE, once as JOIN — and the two buttons do different things:
        // CONTINUE resumes the owned character, JOIN enters with a FRESH one.
        // The second row was an invitation to abandon your own character.
        //
        // The dedupe vector is `world_id`, and it is EXACT rather than a
        // heuristic: both lists ultimately read the same world docs through
        // `GET /api/gateway/world/list`, whose `world_id` is `str(doc["_id"])`
        // — the world entity UUID. Both `_join` and `_continue` post that very
        // value to `/api/worlds/join`, so if two rows carry one `world_id` they
        // are one world by the only definition the join path recognises. (The
        // DISPLAY fields do disagree — `c.world_name` vs `w.name` — which is
        // why the ticket warned a dedupe would have to guess. Don't compare
        // those; they are labels, and 2026-08-06-012-XSTITCH is making one of
        // them host-mutable.)
        const ownedIds = new Set(
            myWorlds.map((c) => c && c.world_id).filter(Boolean)
        );
        // An owned world with no id cannot be deduped against anything. That is
        // a coordinate hole upstream, not a row to quietly drop — say so.
        const unkeyedOwned = myWorlds.filter((c) => !(c && c.world_id));
        if (unkeyedOwned.length) {
            console.warn(
                `[shell] ${unkeyedOwned.length} owned world(s) carry no world_id ` +
                `— cannot dedupe them out of the open list; they may render twice:`,
                unkeyedOwned.map((c) => c && c.world_name)
            );
        }
        let dedupedCount = 0;
        if (ownedIds.size) {
            const before = openWorlds.length;
            const dropped = openWorlds.filter((w) => w && ownedIds.has(w.world_id));
            dedupedCount = dropped.length;
            openWorlds = openWorlds.filter((w) => !(w && ownedIds.has(w.world_id)));
            if (dropped.length) {
                // Never a silent drop (the ticket is explicit about this). Emit
                // what was removed, and surface any case where the two lists
                // agree on the IDENTITY but disagree on the LABEL — that is the
                // untyped-world-identity smell 012 exists to close, and it must
                // stay visible rather than be papered over by this filter.
                console.info(
                    `[shell] lobby dedupe: ${dropped.length} of ${before} open ` +
                    `world(s) hidden as JOIN because you already hold a ` +
                    `character there (CONTINUE only):`,
                    dropped.map((w) => `${w.display_name || w.name} (${w.world_id})`)
                );
                for (const w of dropped) {
                    const mine = myWorlds.find((c) => c && c.world_id === w.world_id);
                    const openLabel = w.display_name || w.name;
                    if (mine && mine.world_name !== openLabel) {
                        console.warn(
                            `[shell] world ${w.world_id} is spelled differently by ` +
                            `the two endpoints: characters/mine says ` +
                            `"${mine.world_name}", worlds/list says "${openLabel}". ` +
                            `Deduped on the id, but the labels disagree ` +
                            `(2026-08-06-012-XSTITCH).`
                        );
                    }
                }
            }
        }

        if (this._screen !== 'lobby') return; // a transition raced ahead
        this._clearScreen();

        const lobby = document.createElement('div');
        lobby.className = 'lobby';

        const mineHtml = myWorlds.length ? `
            <div class="section-head">Your worlds — continue where you have a character</div>
            ${myWorlds.map((c, i) => `
                <div class="world-row mine">
                    <span class="wr-name">${escapeHtml(c.world_name || c.world_id)}</span>
                    <span class="wr-badge">${escapeHtml((c.character_name || '').toUpperCase())}</span>
                    <button class="wr-cta" data-continue="${i}">CONTINUE</button>
                </div>`).join('')}` : '';

        const truncNote = worldsTruncated
            ? `<div class="lobby-note">Showing the first ${openWorlds.length} worlds — more exist than can be listed here.</div>`
            : '';
        // Rows render the LABEL, never the id. As of [2026-08-06-012-XSTITCH]
        // `w.name` IS the minted world id and would show a player a raw UUID;
        // `display_name` is the host's text. The id-fallback stays as the last
        // resort so a row is never blank, but reaching it means the world has
        // no label, which is worth seeing rather than hiding.
        const openHtml = openWorlds.length ? `
            ${openWorlds.map((w, i) => `
                <div class="world-row">
                    <span class="wr-name">${escapeHtml(w.display_name || w.name || w.world_id)}</span>
                    <button class="wr-cta" data-view="${i}">VIEW</button>
                </div>`).join('')}${truncNote}`
            // An empty open list means two different things and they must not
            // wear one message: genuinely no worlds, versus every open world
            // deduped away because you already have a character in all of them.
            // Saying "no open worlds" to a player holding three characters is
            // the same class of lie as a silent drop.
            : (dedupedCount
                ? `<div class="lobby-empty">You already have a character in every open world — continue above, or create a new one.</div>`
                : `<div class="lobby-empty">No open worlds yet — create the first one.</div>`);

        lobby.innerHTML = `
            <div class="lobby-title">WORLDS</div>
            ${mineHtml}
            <div class="section-head">Open worlds — join with a fresh character</div>
            ${openHtml}
            <div class="lobby-actions">
                <button class="btn-ghost" id="shell-create">+ CREATE A NEW WORLD</button>
            </div>`;
        this._screenEl.appendChild(lobby);

        // wire actions
        // An open world's first click is its detail (episodes.md 7.2); JOIN is there.
        lobby.querySelectorAll('[data-view]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const w = openWorlds[parseInt(btn.dataset.view, 10)];
                if (w) this.showWorldDetail(w);
            });
        });
        lobby.querySelectorAll('[data-continue]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const c = myWorlds[parseInt(btn.dataset.continue, 10)];
                if (c) this._continue(c);
            });
        });
        const createBtn = lobby.querySelector('#shell-create');
        if (createBtn) createBtn.addEventListener('click', () => this.showWorldCreate());
    },

    // ---- screen: World detail (storyboard preview/episode-board frame 2) --

    /**
     * An open world's detail: its seats, its Episodes and their routes
     * (episodes.md 7.2), from GET /api/worlds/{id}/detail. Every title arrives
     * rendered from the universe's data; an unnamed one is shown as "unnamed",
     * never as an entity name. A failed read shows its reason and keeps JOIN:
     * the world's board is still reached inside it.
     */
    async showWorldDetail(world) {
        this._screen = 'detail';
        this._setState('world');
        this._renderStatus('Reading the world…');

        let detail = null;
        let failure = null;
        try {
            // No retry: a 503 here names its reason, and JOIN stays on screen.
            detail = await WorldCommands.callWorldAPI(
                `/api/worlds/${encodeURIComponent(world.world_id)}/detail`, 'GET', {},
                { maxRetries: 0, retryableStatus: [], showRetryMessages: false },
            );
            if (!detail || detail.success !== true) {
                failure = (detail && (detail.detail || detail.error)) || 'the world did not answer';
                detail = null;
            }
        } catch (err) {
            failure = (err && err.message) || String(err);
        }
        if (failure) {
            console.warn(`[shell] world detail unread for ${world.world_id}: ${failure}`);
        } else {
            console.info(
                `[shell] world detail served for ${world.world_id}: ` +
                `${detail.episodes.length} Episode(s)`
            );
        }

        if (this._screen !== 'detail') return; // a transition raced ahead
        this._clearScreen();
        const el = document.createElement('div');
        el.className = 'lobby';
        el.innerHTML = this._worldDetailHtml(world, detail, failure);
        this._screenEl.appendChild(el);

        const join = el.querySelector('#shell-detail-join');
        if (join) join.addEventListener('click', () => this._join(world.world_id));
        const back = el.querySelector('#shell-detail-back');
        if (back) back.addEventListener('click', () => this.showLobby());
    },

    _worldDetailHtml(world, detail, failure) {
        const unnamed = '<span class="unnamed">unnamed</span>';
        const titled = (title) => (title == null ? unnamed : escapeHtml(title));
        const label = (detail && detail.display_name) || world.display_name || world.name || world.world_id;

        let meta = '';
        let body;
        if (detail) {
            const seats = detail.seats;
            meta = `
                <div class="detail-meta">
                    ${detail.host ? `<span>Host <b>${escapeHtml(detail.host)}</b></span>` : ''}
                    <span>Seats <b>${escapeHtml(String(seats.held))} of ${escapeHtml(String(seats.capacity))}</b></span>
                </div>`;
            body = detail.episodes.length ? detail.episodes.map((ep) => {
                const routes = ep.routes.map((r) => (r.state === 'full'
                    ? `<div class="muted">${titled(r.title)} — full</div>`
                    : `<div>${titled(r.title)} — ${escapeHtml(String(r.free))} can play</div>`)).join('');
                return `
                <div class="world-row ep">
                    <span class="wr-name">${titled(ep.title)}</span>
                    ${ep.t == null ? '' : `<span class="wr-badge">T${escapeHtml(String(ep.t))}</span>`}
                </div>
                <div class="ep-routes">${routes}</div>`;
            }).join('') : '<div class="lobby-empty">No Episode is authored in this world yet.</div>';
        } else {
            body = `
                <div class="form-error">This world's detail could not be read: ${escapeHtml(failure)}</div>
                <div class="lobby-note">You can still join it; its Episodes are offered inside.</div>`;
        }

        return `
            <div class="lobby-title">${escapeHtml(String(label).toUpperCase())}</div>
            ${meta}
            <div class="section-head">Episodes</div>
            ${body}
            <div class="lobby-actions">
                <button class="btn-primary" id="shell-detail-join">JOIN THIS WORLD</button>
                <button class="btn-ghost" id="shell-detail-back">← back to worlds</button>
            </div>`;
    },

    /** JOIN an open world -> connect (fresh character path). QUEUES on a cold backend. */
    async _join(worldId, joinToken = null) {
        this._renderStatus('Joining…');
        const attempt = async () => {
            const resp = await WorldCommands.callWorldAPI('/api/worlds/join', 'POST', {
                world_id: worldId,
                database: WorldCommands.state.selectedDatabase,
            });
            if (resp && resp.success && resp.connection_details) {
                // A shared link's token rides the connect frame; the GameServer
                // resolves it to the sharer's road and room (game_server/entry.py).
                const details = joinToken
                    ? { ...resp.connection_details, join_token: joinToken }
                    : resp.connection_details;
                await WorldCommands.connectToGameServer(details);
                // FSM CONNECTING -> read-box shown via terminal:connecting event.
                // The connect is fire-and-forget, so the queue waits for FSM
                // progress; if none comes, the backend is cold and we re-attempt.
                return true;
            }
            // [2026-08-08-005-QASTI] A REFUSED join is terminal: the world
            // cannot seat this player (roster exhausted/unprovisioned).
            // Surface the server's copy in the lobby — where the player
            // still is — instead of queueing on it as if it were cold.
            if (resp && resp.refused === true) {
                const refusal = new Error(resp.error || 'This world cannot seat you.');
                refusal.refused = true;
                throw refusal;
            }
            // A join that returns no connection_details is a COLD/UNREADY world,
            // not a refusal — queue on it rather than dead-ending the player.
            throw new Error((resp && resp.error) || 'World not ready yet.');
        };
        this._lastAttempt = attempt;
        await this._queueUntilConnected('Join', attempt);
    },

    /** CONTINUE an owned character -> reuse the proven resume seam. QUEUES on a cold backend. */
    async _continue(character) {
        this._renderStatus('Resuming…');
        const attempt = async () => {
            const resp = await WorldCommands.callWorldAPI('/api/worlds/join', 'POST', {
                world_id: character.world_id,
                database: character.database || WorldCommands.state.selectedDatabase,
            });
            if (resp && resp.success && resp.connection_details) {
                await WorldCommands.connectToGameServer(resp.connection_details);
                return true;
            }
            // [2026-08-08-005-QASTI] Same terminal-refusal contract as _join.
            // (A seat OWNER is admitted by the server gate, so a legitimate
            // continue never sees this; reaching it means the seat is gone.)
            if (resp && resp.refused === true) {
                const refusal = new Error(resp.error || 'This world cannot seat you.');
                refusal.refused = true;
                throw refusal;
            }
            throw new Error((resp && resp.error) || 'World not ready yet.');
        };
        this._lastAttempt = attempt;
        await this._queueUntilConnected('Resume', attempt);
    },

    _lobbyError(msg) {
        this._renderStatus('');
        const err = document.createElement('div');
        err.className = 'form-error';
        err.textContent = msg;
        this._screenEl.appendChild(err);
        const back = document.createElement('div');
        back.className = 'form-hint';
        back.innerHTML = '<span class="link" id="shell-back">‹ back to worlds</span>';
        this._screenEl.appendChild(back);
        const b = back.querySelector('#shell-back');
        if (b) b.addEventListener('click', () => this.showLobby());
    },

    // ---- screen: World Create --------------------------------------------

    async showWorldCreate() {
        this._screen = 'create';
        this._setState('create world');
        this._clearScreen();

        // Universe list from taxonomy (roster-available); single real option today.
        // NOTE: GET /api/worlds/universes currently leaks the `players` namespace/
        // taxonomy artifact as a selectable "universe". It is NOT a playable
        // universe -- filter it out client-side here. The backend should be cleaned
        // to stop emitting it (only real universes belong in this list; `reavers`
        // is the only one that ships today, but do NOT hardcode a whitelist --
        // exclude the known artifact and pass through whatever real universes exist).
        const UNIVERSE_ARTIFACTS = new Set(['players']);
        let universes = [];
        try {
            const fetched = await WorldCommands.fetchAvailableUniverses();
            universes = (Array.isArray(fetched) ? fetched : []).filter(
                (u) => u && u.name && !UNIVERSE_ARTIFACTS.has(String(u.name).toLowerCase())
            );
        } catch (_e) { universes = []; }
        if (this._screen !== 'create') return;
        if (!universes.length) {
            // Fall back to a single Reavers option so the form still renders.
            universes = [{ name: 'reavers', description: 'Reavers' }];
        }

        const stack = document.createElement('div');
        stack.className = 'center-stack';
        const uniChips = universes.map((u, i) =>
            `<span class="chip${i === 0 ? ' on' : ''}" data-uni="${i}">${escapeHtml(u.name)}</span>`).join('');
        // Derived from PAX_MAX_PLAYERS, never typed twice: raising the ceiling is
        // one constant, and the selected chip cannot drift from the value the
        // form actually submits.
        const maxChips = MAX_PLAYER_CHOICES.map((n) => (
            n === PAX_MAX_PLAYERS
                ? `<span class="chip on" data-max="${n}">${n}</span>`
                : `<span class="chip disabled" data-max="${n}" aria-disabled="true" style="${INERT_STYLE}">${n}</span>`
        )).join('\n                        ');
        stack.innerHTML = `
            <div class="brand" style="font-size:1.3rem;">CREATE A WORLD</div>
            <div class="brand-sub">A new world, yours to host</div>
            <div class="field"><label>World name</label>
                <input class="input" id="cw-name" type="text" placeholder="My world" maxlength="60"></div>
            <div class="field"><label>Universe</label>
                <div class="toggle-row" id="cw-uni">${uniChips}</div></div>
            <div class="row2">
                <div class="field"><label>Max players</label>
                    <div class="toggle-row" id="cw-max">
                        ${maxChips}
                    </div></div>
                <div class="field"><label>Visibility</label>
                    <div class="toggle-row" id="cw-vis">
                        <span class="chip on" data-vis="public">Public</span>
                        <span class="chip disabled" data-vis="private" aria-disabled="true" style="${INERT_STYLE}">Private</span>
                    </div></div>
            </div>
            <div class="field" id="cw-pw-field"><label>Password (private worlds)</label>
                <input class="input placeholder disabled" id="cw-pw" type="text" disabled readonly aria-disabled="true" style="${INERT_STYLE}" placeholder="— unavailable in this build —">
                <div class="form-hint" id="cw-pw-hint" style="margin-top:0.35rem;text-align:left;">Password-protected worlds are not available in this build.</div></div>
            <button class="btn-primary" id="cw-submit">CREATE WORLD</button>
            <div class="form-error" id="cw-error"></div>
            <div class="form-hint"><span class="link" id="cw-back">‹ back to lobby</span></div>`;
        this._screenEl.appendChild(stack);

        // chip-group single-select behavior
        const selectChip = (groupSel, chip) => {
            stack.querySelectorAll(`${groupSel} .chip`).forEach((c) => c.classList.remove('on'));
            chip.classList.add('on');
        };
        const state = { universe: universes[0].name, maxPlayers: PAX_MAX_PLAYERS, visibility: 'public' };
        stack.querySelectorAll('#cw-uni .chip').forEach((c) => c.addEventListener('click', () => {
            selectChip('#cw-uni', c); state.universe = universes[parseInt(c.dataset.uni, 10)].name;
        }));
        stack.querySelectorAll('#cw-max .chip').forEach((c) => c.addEventListener('click', () => {
            // THE gate. A greyed chip that still ran this body would move `.on`
            // AND `state.maxPlayers` while looking inert -- the exact CSS-only
            // failure the ceiling is meant to rule out. Refuse before anything
            // mutates, so a click on 4/16/32 is a genuine no-op.
            if (c.classList.contains('disabled')) return;
            selectChip('#cw-max', c); state.maxPlayers = parseInt(c.dataset.max, 10);
        }));
        const pwField = stack.querySelector('#cw-pw-field');
        // PRIVATE IS WITHDRAWN FOR PAX (project owner, 2026-09-01), and it is
        // greyed for the same reason the password is: it no longer does
        // anything. `state.visibility` was read in exactly ONE place -- the
        // password branch of the submit handler -- and it was never sent to the
        // server; the create flow does not write `is_public` at all. So Private
        // never made a world private, it only revealed the password box, and
        // with the box withdrawn the chip changes nothing at all.
        //
        // A live control that does nothing is worse than an absent one: it
        // teaches the player to distrust every other control on the screen. Same
        // guard as the seat chips -- refused BEFORE `selectChip` and before
        // `state.visibility` is written, so it is inert in behaviour and not
        // only in appearance.
        stack.querySelectorAll('#cw-vis .chip').forEach((c) => c.addEventListener('click', () => {
            if (c.classList.contains('disabled')) return;
            selectChip('#cw-vis', c); state.visibility = c.dataset.vis;
            pwField.style.display = state.visibility === 'private' ? '' : 'none';
        }));
        pwField.style.display = 'none';

        stack.querySelector('#cw-back').addEventListener('click', () => this.showLobby());
        stack.querySelector('#cw-submit').addEventListener('click', () => {
            const name = (stack.querySelector('#cw-name').value || '').trim();
            const errEl = stack.querySelector('#cw-error');
            if (!name) { errEl.textContent = 'A world needs a name.'; return; }
            errEl.textContent = '';
            this._runCreate({
                name,
                universe: state.universe,
                // Left reading from `state` on purpose. Substituting the constant
                // here would make the form submit 8 even if the chip guard above
                // were deleted -- a check that cannot fail. The guard is the
                // enforcement; this line is what proves it worked.
                maxPlayers: state.maxPlayers,
                // WITHDRAWN for the PAX build. The field is inert, so there is no
                // value to read and no `state.visibility` branch left to make;
                // `_runCreate`'s `password_setup` step therefore always answers
                // 'none'. ⚠️ This also makes the Visibility chips a no-op -- they
                // fed nothing else. See the note on that group below.
                password: '',
            });
        });
    },

    /**
     * Drive the existing HTTP create flow (/api/worlds/create then
     * /api/worlds/create/input per step). We collected every field up front and
     * feed whichever step the backend asks for, so no net-new endpoint is needed.
     */
    async _runCreate(form) {
        this._renderStatus('Creating world…');
        try {
            let resp = await WorldCommands.callWorldAPI('/api/worlds/create', 'POST', {
                database: WorldCommands.state.selectedDatabase,
                // THE opt-in. A world is tagged `test` and hidden from the
                // lobby unless its creator declares itself player-facing, and
                // this create screen is the only surface that does
                // ([2026-08-07-004-QASTI]). The test harnesses drive this exact
                // same flow, so the server cannot tell them from a real host —
                // the declaration has to come from the client, and the default
                // has to be the safe one. Do NOT copy this line into a harness.
                player_facing: true,
            });
            if (resp && resp.action === 'show_limits') {
                this._createError('World creation limit reached — try again later.');
                return;
            }
            if (!resp || !resp.success || !resp.flow) {
                this._createError((resp && resp.error) || 'Could not start world creation.');
                return;
            }

            const valueForStep = (step) => {
                switch (step) {
                    case 'universe_selection':
                        // backend accepts the universe NAME at this step; also pins the DB
                        WorldCommands.state.selectedDatabase = `plug_engine_${form.universe}`;
                        return form.universe;
                    case 'world_name': return form.name;
                    case 'password_setup': return form.password ? form.password : 'none';
                    case 'max_players': return String(form.maxPlayers);
                    case 'confirmation': return 'confirm';
                    default: return '';
                }
            };

            let step = resp.flow.step;
            let guard = 0;
            while (guard++ < 12) {
                const value = valueForStep(step);
                resp = await WorldCommands.callWorldAPI('/api/worlds/create/input', 'POST', {
                    input: value,
                    database: WorldCommands.state.selectedDatabase,
                });
                if (resp && resp.completed) {
                    if (resp.world_info) {
                        // ⚠️ THIS is the path a brand-new world takes, and it is the
                        // COLDEST connect in the product: the GameServer was just
                        // allocated and GANDALF may be scaled to zero. Before the
                        // queue, `connectToGameServer` was fire-and-forget here —
                        // if the socket never opened, the player sat on
                        // "Reaching the world…" forever, with the world allocated
                        // and the GameServer logging nothing at all.
                        const world = resp.world_info;
                        const attempt = async () => {
                            await WorldCommands.connectToGameServer(world);
                            return true;
                        };
                        this._lastAttempt = attempt;
                        await this._queueUntilConnected('Enter', attempt);
                        return;
                    }
                    this._createError('World created, but no connection details were returned.');
                    return;
                }
                if (resp && resp.cancelled) { this.showLobby(); return; }
                if (resp && resp.success === false) {
                    this._createError(resp.error || 'Invalid value — please adjust and retry.');
                    return;
                }
                if (resp && resp.flow && resp.flow.step) {
                    step = resp.flow.step;
                    continue;
                }
                this._createError('Unexpected response during world creation.');
                return;
            }
            this._createError('World creation did not complete.');
        } catch (err) {
            this._createError(err.message || 'World creation failed.');
        }
    },

    _createError(msg) {
        // Re-render the form path with the error shown at the top of a status card.
        this._renderStatus('');
        const err = document.createElement('div');
        err.className = 'form-error';
        err.textContent = msg;
        this._screenEl.appendChild(err);
        const back = document.createElement('div');
        back.className = 'form-hint';
        back.innerHTML = '<span class="link" id="shell-cerr-back">‹ back to create</span>';
        this._screenEl.appendChild(back);
        const b = back.querySelector('#shell-cerr-back');
        if (b) b.addEventListener('click', () => this.showWorldCreate());
    },

    // ---- screen: read-box + sigil ----------------------------------------

    showReadBox(statusLabel, subText, warm = false) {
        // Reuse an existing read-box screen (just update status) so paging state
        // survives across CONNECTING -> QUEUED -> WELCOME_NARRATION transitions.
        if (this._screen === 'readbox' && this._readbox) {
            this._setReadBoxStatus(statusLabel, subText, warm);
            return;
        }
        this._screen = 'readbox';
        this._setState('connecting');
        this._showOverlay(true);
        this._clearScreen();

        const stack = document.createElement('div');
        stack.className = 'center-stack';
        stack.innerHTML = `
            <div class="queue-head">
                <div class="queue-status" id="shell-qstatus">${escapeHtml(statusLabel)}</div>
                <div class="queue-sub" id="shell-qsub">${subText || ''}</div>
            </div>`;
        this._screenEl.appendChild(stack);

        this._sigil = new DecoderSigil();
        this._sigil.mount(stack, { warm });

        this._readbox = new ReadBox({ escapeHtml });
        this._readbox.mount(stack);

        this._setReadBoxStatus(statusLabel, subText, warm);
    },

    _setReadBoxStatus(label, subText, warm) {
        const st = document.getElementById('shell-qstatus');
        const sub = document.getElementById('shell-qsub');
        if (st) { st.textContent = label; st.classList.toggle('warm', !!warm); }
        if (sub && subText != null) sub.innerHTML = subText;
        if (this._sigil) this._sigil.setWarm(!!warm);
    },

    /**
     * Mirror a queue frame into the read-box status/sub line.
     *
     * [2026-08-06-006-QASTI] Two frames land here, and the read-box is the
     * surface the player is actually looking at (the terminal block behind the
     * overlay is hidden). `admitted` is the `queue_admitted` repaint: the line is
     * over, so the status leaves WARMING for ENTERING and the position clause is
     * dropped rather than left frozen on a place the player no longer holds.
     * Still not `warm` — warm belongs to READY, when the world is actually open.
     */
    _updateQueue(message) {
        if (this._screen !== 'readbox') {
            this.showReadBox('WARMING', '');
        }
        if (message && message.admitted) {
            this._setReadBoxStatus(
                'ENTERING',
                escapeHtml("You're through the queue. Opening the world…"),
                false
            );
            return;
        }
        const pos = (typeof message.position === 'number') ? message.position : null;
        const total = (typeof message.total_waiting === 'number') ? message.total_waiting : null;
        const serverMsg = (message.message || 'Servers are warming up.').trim();
        let sub = escapeHtml(serverMsg);
        if (pos != null && total != null) sub += ` Position <b>${pos}</b> of <b>${total}</b> in line.`;
        else if (pos != null) sub += ` Position <b>${pos}</b> in line.`;
        this._setReadBoxStatus('WARMING', sub, false);
    },

    // ---- screen: Character Choice ----------------------------------------

    /**
     * @param {{reclaim?: boolean}} [opts] `reclaim` = this offer follows a
     *        death, not a first join.
     */
    showCharacterChoice(opts) {
        const reclaim = !!(opts && opts.reclaim);
        this._screen = 'character';
        this._reclaim = reclaim;
        this._setState(reclaim ? 'take up a vessel' : 'choose your character');
        this._showOverlay(true);
        this._clearScreen();

        const roster = this._roster;
        const chars = (roster && roster.characters) || [];

        const wrap = document.createElement('div');
        wrap.style.width = '100%';
        // Canon (ui-visual-canon §5 + drift fix): mortals of Tritar, NOT Reavers;
        // triad Fighter/Rogue/Mage; earnable ascended-epithet is aspiration only;
        // occupancy noun is "players". Copy below is canon-honest.
        //
        // [2026-07-29-012-QASTI] THE COPY IS DERIVED FROM THE ROSTER IT IS
        // RENDERING. "Three of Tritar stand ready" was HARDCODED and printed
        // above ONE card on the post-death screen — a visible contradiction on
        // the most emotionally loaded screen in the game. A count is a fact
        // about the payload; it is never a constant.
        wrap.innerHTML = `
            <div class="char-title">${reclaim ? 'TAKE UP A VESSEL' : 'CHOOSE YOUR CHARACTER'}</div>
            ${reclaim ? this._deathRestatement() : ''}
            <div class="char-sub">${this._rosterSubCopy(chars.length, reclaim)}</div>
            <div class="char-grid" id="shell-char-grid"></div>`;
        this._screenEl.appendChild(wrap);

        const grid = wrap.querySelector('#shell-char-grid');
        if (!chars.length) {
            grid.innerHTML = `<div class="lobby-empty">No characters were offered. <span class="link" id="shell-char-back">‹ back to worlds</span></div>`;
            const b = grid.querySelector('#shell-char-back');
            if (b) b.addEventListener('click', () => this.showLobby());
            return;
        }

        chars.forEach((char) => {
            const card = document.createElement('div');
            card.className = 'char-card';
            // Collapse whitespace runs so a card name degrades cleanly when the
            // archetype field is absent: server composes the title as
            // "<name> - <archetype> of Time"; with archetype empty that yields
            // "Warden Garrick -  of Time" (double space). Collapse + trim to
            // "Warden Garrick - of Time". (Post-deploy #324 populates archetype.)
            const name = (char.title || char.name || `Choice ${char.choice}`)
                .replace(/\s{2,}/g, ' ').trim();
            const portrait = char.image_url
                ? `<img src="${escapeHtml(char.image_url)}" alt="${escapeHtml(name)}">`
                : `[ portrait ]`;
            // archetype/epithet are surfaced ONLY when the payload carries them
            // (canon-honest: we do not fabricate an ascended-epithet claim).
            const archHtml = char.archetype
                ? `<div class="char-arch">${escapeHtml(char.archetype)}</div>` : '';
            const blurb = char.description || char.blurb || '';
            const epithet = char.ascended_epithet || char.epithet;
            const epithetHtml = epithet
                ? `<div class="char-epithet">may one day earn <span>${escapeHtml(epithet)}</span></div>` : '';
            card.innerHTML = `
                <div class="char-portrait">${portrait}</div>
                <div class="char-body">
                    <div class="char-name">${escapeHtml(name)}</div>
                    ${archHtml}
                    <div class="char-blurb">${escapeHtml(blurb)}</div>
                    ${epithetHtml}
                    <button class="char-pick" data-choice="${escapeHtml(String(char.choice))}">CHOOSE</button>
                </div>`;
            grid.appendChild(card);

            card.querySelector('.char-pick').addEventListener('click', () => {
                grid.querySelectorAll('.char-card').forEach((c) => c.classList.remove('sel'));
                card.classList.add('sel');
                card.querySelector('.char-pick').textContent = 'SELECTED';
                this._pickCharacter(char.choice);
            });
        });

        this._mountCharacterPrompt(wrap, chars);
    },

    // ---- screen: the Episode board (storyboard preview/episode-board 3-6) --

    /**
     * The void's board, or the hand dealt for one of its roads, through the one
     * board view (`episode-board-view.js`) the terminal also mounts. Its host
     * element outlives a screen clear, so a board offered again keeps the
     * Episode the player opened and names the road that filled.
     */
    showBoard() {
        const selection = this._boardSelection;
        const V = window.EpisodeBoardView;
        if (!selection || !V) {
            console.error('[shell] board screen with no board or no view loaded', selection);
            return;
        }
        this._screen = 'board';
        this._setState(selection.hand ? 'choose your character' : 'choose your road');
        this._showOverlay(true);
        this._clearScreen();
        if (!this._boardHost) {
            this._boardHost = document.createElement('div');
            this._boardHost.className = 'eb-host';
            this._boardView = V.createBoardView(document, this._boardHost, (text) => this._answerBoard(text));
        }
        this._screenEl.appendChild(this._boardHost);
        if (selection.hand) {
            this._boardView.showHand(selection.hand);
        } else {
            this._boardView.showBoard(selection.board);
        }
    },

    /** Answer the board as the terminal would: a road's number, BACK, or a card's number. */
    _answerBoard(text) {
        const selection = this._boardSelection;
        if (selection && selection.hand && /^\d+$/.test(text)) {
            this._pickCharacter(text);
            return;
        }
        const GSC = window.GameServerConnection;
        if (GSC && typeof GSC.processInput === 'function') {
            GSC.processInput(text);
        } else {
            console.warn('[shell] GameServerConnection.processInput unavailable for the board');
        }
    },

    /**
     * [2026-07-29-012-QASTI] Count copy DERIVED from the roster.
     * `_rosterSubCopy(1, …)` may never say "Three".
     */
    _rosterSubCopy(n, reclaim) {
        const WORDS = ['no', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'];
        const word = WORDS[n] || String(n);
        const verb = (n === 1) ? 'stands' : 'stand';
        if (reclaim) {
            return `${word} of Tritar ${verb} ready to carry you back. `
                + 'Take one up, and the world will answer you again.';
        }
        return `${word} of Tritar ${verb} ready — mortals still, each a long road from `
            + 'ascension. Choose, and step into the world.';
    },

    /**
     * [2026-07-29-012-QASTI] DoD 2 — the overlay may not destroy a beat the
     * player has not read. The roster-offer hold (client-side) already waits for
     * the death scene to finish delivering before this screen is allowed to
     * appear; re-stating the scene here is the second half: whatever the player
     * did or did not finish reading in the terminal is still in front of them.
     *
     * Empty string when there is no scene to re-state — never a placeholder.
     */
    _deathRestatement() {
        let text = '';
        try {
            const GSC = window.GameServerConnection;
            text = (GSC && GSC._lastSceneText) ? String(GSC._lastSceneText) : '';
        } catch (_e) {
            text = '';
        }
        if (!text.trim()) return '';
        return `<div class="char-fell"><div class="char-fell-label">YOU FELL</div>`
            + `<div class="char-fell-text">${escapeHtml(text)}</div></div>`;
    },

    /**
     * [2026-07-29-002-QASTI] DoD 3 — THE PROMISED INPUT PATH.
     *
     * The client logs "Awaiting character choice (1, 2, or 3)" and the terminal
     * FSM opens input for CHARACTER_CHOICE — but this overlay sets
     * `.main { display:none }`, so the `.active-input-line` the FSM creates is
     * inside a hidden subtree and `#input` cannot be focused or typed. On prod
     * the driver waited 60s for a visible input line, then probed 100s for typed
     * progress, and the claim landed only by CLICKING a card. That was true for
     * the FIRST-JOIN screen too — the copy has promised a keyboard path on a
     * surface that had none since the shell cutover.
     *
     * So the promise is honoured WHERE THE ROSTER IS SHOWN: a prompt line on the
     * screen itself, accepting `1/2/3` or a vessel name, routed through the same
     * `_pickCharacter` the CHOOSE button uses. Clicking is unchanged.
     *
     * The class is `shell-char-input-line`, deliberately NOT `.active-input-line`
     * — the pump queries that selector to decide narration placement and prompt
     * creation (`_shouldShowPrompt`), and a second node answering it would
     * poison those decisions.
     */
    _mountCharacterPrompt(wrap, chars) {
        if (!chars || !chars.length) return null;
        // Built node-by-node rather than through innerHTML: the prompt is the
        // one part of this screen with BEHAVIOUR, and behaviour has to be
        // reachable by a pin without an HTML parser in the loop.
        const host = document.createElement('div');
        host.className = 'shell-char-input-line';

        const caret = document.createElement('span');
        caret.className = 'shell-char-caret';
        caret.textContent = ']';
        host.appendChild(caret);

        const field = document.createElement('input');
        field.className = 'shell-char-input';
        field.id = 'shell-char-input';
        field.type = 'text';
        field.setAttribute('autocomplete', 'off');
        field.setAttribute('spellcheck', 'false');
        field.setAttribute('aria-label', 'type a number or a name to choose a vessel');
        host.appendChild(field);

        const hint = document.createElement('div');
        hint.className = 'shell-char-hint';
        hint.id = 'shell-char-hint';
        hint.textContent = `type ${chars.length === 1 ? '1' : `1-${chars.length}`}`
            + ' or a name, then ENTER — or click a card';
        host.appendChild(hint);

        wrap.appendChild(host);
        this._charPromptField = field;

        field.addEventListener('keydown', (ev) => {
            if (ev.key !== 'Enter') return;
            if (typeof ev.preventDefault === 'function') ev.preventDefault();
            const raw = String(field.value || '').trim();
            if (!raw) return;
            const choice = this._resolveChoiceInput(raw, chars);
            if (choice == null) {
                field.value = '';
                hint.textContent = `"${raw}" is not one of these. Type a number or a name.`;
                hint.classList.add('bad');
                return;
            }
            field.value = '';
            this._pickCharacter(choice);
        });
        try { field.focus(); } catch (_e) { /* focus is best-effort */ }
        return host;
    },

    /**
     * Resolve typed text to a `choice` value, or null when it names nothing.
     *
     * Accepts what the copy promises and what a human actually types: the
     * ordinal shown on the card, or any distinctive part of the vessel's name
     * ("garrick" for "Warden Garrick - of Time"). A bare number is only a choice
     * if a card carries it — otherwise it falls through to the name match rather
     * than silently claiming a neighbour.
     */
    _resolveChoiceInput(raw, chars) {
        const text = String(raw || '').trim();
        if (!text || !chars || !chars.length) return null;
        for (const c of chars) {
            if (String(c.choice) === text) return c.choice;
        }
        const needle = text.toLowerCase();
        const hits = chars.filter((c) => {
            const name = String(c.title || c.name || '').toLowerCase();
            return name.includes(needle);
        });
        // Ambiguity is a REFUSAL, not a coin flip — the same discipline the
        // entity read path uses. Two vessels matching "of Time" is a question,
        // not an answer.
        return (hits.length === 1) ? hits[0].choice : null;
    },

    /** Submit the pick via the SAME path the terminal 1/2/3 uses (zero backend edit). */
    _pickCharacter(choice) {
        const GSC = window.GameServerConnection;
        if (GSC && typeof GSC.processInput === 'function') {
            GSC.processInput(String(choice));
            // Server responds with creating -> spawn -> spawn_complete; FSM drives
            // SPAWNING (read-box "entering") -> GAMEPLAY (handoff).
            this.showReadBox('READY', 'Weaving you into the world…', true);
        } else {
            console.warn('[shell] GameServerConnection.processInput unavailable for pick');
        }
    },
};

if (typeof window !== 'undefined') {
    window.Shell = Shell;
}

export default Shell;
